import { forwardRef } from "react";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Badge } from "../../../../../components/ui/badge";
import { OptroOpsAuditSummary } from "../../../../../models/audit/workStep";

const EMPTY_VALUE = "–";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Formats a DateOnly wire string ("YYYY-MM-DD") long-form ("October 1, 2026")
// from its parts — parsing via Date would interpret it as UTC midnight and
// shift a day in western timezones.
function formatDateOnly(value: string): string | null {
  const [year, month, day] = value.split("-").map(Number);
  const monthName = MONTH_NAMES[month - 1];
  if (!year || !monthName || !day) return null;
  return `${monthName} ${day}, ${year}`;
}

function formatDateRange(start: string | null, end: string | null): string {
  const from = start ? formatDateOnly(start) : null;
  const to = end ? formatDateOnly(end) : null;
  if (from && to) return `${from} to ${to}`;
  return from ?? to ?? "";
}

interface SelectOpsAuditRowProps {
  audit: OptroOpsAuditSummary;
  checked: boolean;
  onToggle: (audit: OptroOpsAuditSummary) => void;
}

// One selectable row of the import wizard's audit-selection table.
export const SelectOpsAuditRow = forwardRef<
  HTMLTableRowElement,
  SelectOpsAuditRowProps
>(({ audit, checked, onToggle }, ref) => {
  const dateRange = formatDateRange(audit.startDate, audit.endDate);
  return (
    <TableRow
      ref={ref}
      className="cursor-pointer"
      onClick={() => onToggle(audit)}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggle(audit)}
          aria-label={`Select ${audit.name}`}
        />
      </TableCell>
      <TableCell>
        <div className="font-medium">{audit.name}</div>
        <div className="text-sm text-stone-500">
          {audit.ref}
          {dateRange && ` · ${dateRange}`}
        </div>
      </TableCell>
      <TableCell className="text-stone-600">
        {audit.category ?? EMPTY_VALUE}
      </TableCell>
      <TableCell className="text-stone-600">
        {audit.period ?? EMPTY_VALUE}
      </TableCell>
      <TableCell className="text-stone-600">
        {audit.lead ?? EMPTY_VALUE}
      </TableCell>
      <TableCell>
        {/* The endpoint lists Active audits only, so the badge is constant. */}
        <Badge
          variant="outline"
          className="text-sky-700 border-sky-200 bg-sky-50"
        >
          Active
        </Badge>
      </TableCell>
      <TableCell className="text-right text-stone-600 tabular-nums">
        {audit.workStepCount ?? EMPTY_VALUE}
      </TableCell>
    </TableRow>
  );
});

SelectOpsAuditRow.displayName = "SelectOpsAuditRow";
